import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import InlineDisclaimers from './InlineDisclaimers';
import { DataStore, SuperscriptScope } from '../stores';
import TabRegion from '../models/components/TabRegion';
import { Theme } from '../models/Theme';

/**
 * This can't be a button as there can be a tooltip button inside of this element
 * ~ the browser throws warnings for buttons inside of other buttons.
 */
const TabItem = styled<any, any>('div', 'tabItem', 'tab')`
    ${propStyles};
    outline: none;
    cursor: pointer;
    opacity: ${p => (p.disabled ? 0.5 : 1)};
`;

TabItem.defaultProps = {
    flex: '0 1 auto',
    fontSize: ['sm', 'md'],
    fontFamily: 'heading',
    fontWeight: 'normal',
    color: 'body',
    paddingX: 'md',
    paddingY: 'sm',
    bgColor: 'transparent',
    borderStyle: 'none',
    selected: {
        color: 'primary',
        borderWidth: '2px',
        borderStyle: 'none none solid none',
        borderColor: 'primary',
    },
};

export interface TabAccessibility {
    label?: string;
    description?: string;
    ownerPrefix?: string;
}
export interface TabProps {
    id: string;
    region: TabRegion;
    onClick?: (e) => void;
    onKeyDown?: (e) => void;
    theme?: Theme;
    themeOverride?: Theme;
    isSelected?: boolean;
    accessibility?: TabAccessibility;
    metricContext?: string;
    conditionalDisclaimers?: ConditionalDisclaimers;
    dataStore?: DataStore;
    isFocused?: boolean;
    getTabReference?: (instance: HTMLElement) => void;
    superscriptScope?: SuperscriptScope;
}

@inject('dataStore')
@observer
class Tab extends React.Component<TabProps> {
    static defaultProps = {
        accessibility: {
            label: '',
            description: '',
        },
    };

    ref: HTMLElement | null | undefined;

    componentDidMount() {
        this.setFocus();
    }

    componentDidUpdate() {
        this.setFocus();
    }

    setFocus() {
        const { isFocused } = this.props;
        if (isFocused && this.ref) {
            this.ref.focus();
        }
    }

    assignReference = (instance: HTMLElement | null) => {
        const { getTabReference } = this.props;
        this.ref = instance;
        if (getTabReference) {
            return getTabReference(instance);
        }
    };

    render() {
        const {
            id,
            region,
            theme,
            themeOverride,
            children,
            onClick,
            onKeyDown,
            isSelected,
            accessibility,
            metricContext,
            conditionalDisclaimers,
            superscriptScope,
            dataStore,
            ...props
        } = this.props;

        const accessibilityLabel = `${accessibility.label} ${accessibility.description}`.trim();
        const ariaLabel = accessibilityLabel || children;

        return (
            <TabItem
                ref={this.assignReference}
                role="tab"
                id={`tab-${region}-${id}`}
                aria-controls={`tabpanel-${region}-${id}`}
                aria-label={ariaLabel}
                // used to tell screen reader if the tab is selected
                aria-selected={isSelected}
                onClick={onClick}
                onKeyDown={onKeyDown}
                data-selected={isSelected}
                data-metric-id={`tab-${metricContext}`}
                tabIndex={isSelected ? '0' : '-1'}
                {...theme.Tab}
                {...themeOverride}
                {...props}
            >
                <Translate>{children}</Translate>
                <InlineDisclaimers
                    conditionalDisclaimers={conditionalDisclaimers}
                    accessibilityContext={{
                        key: `More information about the {label} tab`,
                        placeholders: {
                            label: id
                        }
                    }}
                    forceSuperScripts={dataStore.isMobile}
                    superscriptScope={superscriptScope}
                    analyticsContext={{
                        metricContext,
                        source: 'tab',
                        sourceContext: children,
                    }}
                />
            </TabItem>
        );
    }
}

export default withTheme(Tab) as any;



// WEBPACK FOOTER //
// ./src/Components/Tab.tsx